import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  type PersonName,
  parsePartialDate,
  parsePersonName,
  tidyParts,
  yearOf,
} from "@/lib/tool-kit/citation-parts";
import {
  type BibTextOptions,
  bibText,
  bibtexName,
  citationKey,
  formatBibEntry,
  joinNames,
  looksLikeOrganization,
  monthMacro,
} from "@/lib/tool-kit/identifiers/bibtex";
import {
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  type CffMap,
  type CffNode,
  cffList,
  cffMap,
  cffText,
} from "@/lib/tool-kit/special-sources/cff";
import {
  apaInText,
  doiUrl,
  type SpecialValues,
} from "@/lib/tool-kit/special-sources/common";

/*
 * Software on GitHub — the repository, or the archived release a CITATION.cff points
 * to — in APA 7, MLA 9 and Chicago 18, with BibTeX and biblatex entries.
 *
 * Sources checked (October 2026):
 * - APA 7 §10.10 (software): "Borenstein, M., … (2014). Comprehensive meta-analysis
 *   (Version 3.3.070) [Computer software]. Biostat. https://www.meta-analysis.com/"
 *   (as APA's "Elements of reference list entries" page quotes it): author, date,
 *   title, version in parentheses, the bracketed description, publisher, DOI or URL.
 *   A DOI replaces the URL; software archived on Zenodo names Zenodo as publisher.
 *   APA has no GitHub example; library guides name GitHub as the publisher.
 * - MLA 9: MLA Style Center, "Citing Source Code in MLA Style" (J. Wallace, 2018):
 *   the repository owner is the author, the software's name is an italicized title
 *   whose capitalization is kept as the developers write it, the version (or commit)
 *   follows, and GitHub is a second container with no publisher, followed by the URL.
 * - Chicago 18 §14.169 (software and apps), as the Citation Style Language's CMOS 18
 *   style renders it: "Stephan Druskat et al., Citation File Format, version 1.2.0
 *   (Zenodo, 2021), https://doi.org/10.5281/zenodo.5171937." and "Druskat, Stephan,
 *   … Citation File Format. Version 1.2.0. Zenodo, 2021. https://doi.org/…".
 * - CITATION.cff: Citation File Format 1.2.0 schema guide — authors as person
 *   (family-names, given-names, name-particle, name-suffix) or entity (name);
 *   version, doi, identifiers, date-released, url, repository-code, and
 *   preferred-citation, "a reference to another work that should be cited instead".
 *   GitHub's "Cite this repository" shows the preferred citation when there is one
 *   ("About CITATION files", GitHub Docs), and outputs APA and BibTeX.
 * - BibTeX has no @software type; biblatex does (it is an alias of @misc), so both
 *   are offered — @misc with the version in `note`, and @software with `version`.
 */

export const GITHUB_FIELDS = {
  authors: "authors",
  title: "title",
  version: "version",
  date: "date",
  publisher: "publisher",
  doi: "doi",
  url: "url",
} as const;

const F = GITHUB_FIELDS;

export interface GithubLabels {
  apa: string;
  mla: string;
  chicago: string;
  bibtex: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  bibliography: string;
  bibtexMisc: string;
  biblatexSoftware: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
  bibtexNote: string;
}

/* ----------------------------------------------------------- repository URL */

const RE_GITHUB_PATH =
  /^(?:https?:\/\/)?(?:www\.)?github\.com[/:]([A-Za-z0-9-]+)\/([A-Za-z0-9._-]+?)(?:\.git)?(?:[/?#].*)?$/i;
const RE_SSH =
  /^git@github\.com:([A-Za-z0-9-]+)\/([A-Za-z0-9._-]+?)(?:\.git)?$/i;
const RE_SHORT = /^([A-Za-z0-9-]+)\/([A-Za-z0-9._-]+)$/;

export interface RepoRef {
  owner: string;
  repo: string;
}

/** "https://github.com/owner/repo/tree/main", "git@github.com:owner/repo.git", "owner/repo". */
export function parseRepoInput(input: string): RepoRef | null {
  const text = input.trim();
  const match =
    RE_GITHUB_PATH.exec(text) ?? RE_SSH.exec(text) ?? RE_SHORT.exec(text);
  if (!match) {
    return null;
  }
  return { owner: match[1], repo: match[2] };
}

/* ------------------------------------------------------------------ authors */

const RE_BRACED = /^\{(.+)\}$/;
const GROUP_WORDS = new Set([
  "developers",
  "contributors",
  "community",
  "maintainers",
  "authors",
  "lab",
]);
const RE_WORDS = /[\s,.]+/;

/**
 * One author per line. A group — "The pandas development team", "{Mozilla}" in
 * braces — is kept whole and never inverted.
 */
export function parseSoftwareAuthors(text: string): PersonName[] {
  const people: PersonName[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (line === "") {
      continue;
    }
    const braced = RE_BRACED.exec(line);
    const group =
      braced ||
      looksLikeOrganization(line) ||
      line
        .toLowerCase()
        .split(RE_WORDS)
        .some((word) => GROUP_WORDS.has(word));
    if (group) {
      people.push({
        given: [],
        family: braced ? braced[1].trim() : line,
        organization: true,
      });
      continue;
    }
    const person = parsePersonName(line);
    if (person) {
      people.push(person);
    }
  }
  return people;
}

/* ------------------------------------------------------------- CITATION.cff */

function cffPerson(node: CffNode): string {
  const map = cffMap(node);
  if (!map) {
    return "";
  }
  const name = cffText(map.name);
  if (name) {
    return `{${name}}`;
  }
  const family = [cffText(map["name-particle"]), cffText(map["family-names"])]
    .filter(Boolean)
    .join(" ");
  const given = cffText(map["given-names"]);
  const suffix = cffText(map["name-suffix"]);
  if (family === "") {
    return given;
  }
  const inverted = given ? `${family}, ${given}` : family;
  return suffix ? `${inverted}, ${suffix}` : inverted;
}

/** The authors of a CFF block, one per line, as the form takes them. */
export function cffAuthorsText(node: CffNode | undefined): string {
  return cffList(node).map(cffPerson).filter(Boolean).join("\n");
}

/** The DOI: the `doi` key, or the first DOI among `identifiers`. */
export function cffDoi(doc: CffMap): string {
  const direct = cffText(doc.doi);
  if (direct) {
    return direct;
  }
  for (const item of cffList(doc.identifiers)) {
    const map = cffMap(item);
    if (map && cffText(map.type) === "doi" && cffText(map.value)) {
      return cffText(map.value);
    }
  }
  return "";
}

const RE_ZENODO = /^10\.5281\/zenodo\./i;

/** Form values from a CITATION.cff's top level (the software itself). */
export function valuesFromCff(
  doc: CffMap,
  repositoryUrl: string
): Record<string, string> {
  const doi = cffDoi(doc);
  return {
    [F.authors]: cffAuthorsText(doc.authors),
    [F.title]: cffText(doc.title),
    [F.version]: cffText(doc.version),
    [F.date]: cffText(doc["date-released"]),
    [F.publisher]: RE_ZENODO.test(doi) ? "Zenodo" : "GitHub",
    [F.doi]: doi,
    [F.url]: cffText(doc["repository-code"]) || repositoryUrl,
  };
}

/** The work a CITATION.cff asks to be cited instead, as article-style values. */
export interface PreferredCitation {
  type: string;
  values: Record<string, string>;
}

export function preferredCitation(doc: CffMap): PreferredCitation | null {
  const pref = cffMap(doc["preferred-citation"]);
  if (!pref) {
    return null;
  }
  const start = cffText(pref.start);
  const end = cffText(pref.end);
  const date = cffText(pref["date-published"]);
  const journal =
    cffText(pref.journal) ||
    cffText(pref["collection-title"]) ||
    cffText(cffMap(pref.conference)?.name);
  return {
    type: cffText(pref.type) || "generic",
    values: {
      authors: cffAuthorsText(pref.authors),
      year: cffText(pref.year) || date.slice(0, 4),
      title: cffText(pref.title),
      journal,
      volume: cffText(pref.volume),
      issue: cffText(pref.issue),
      pages: start && end ? `${start}–${end}` : start,
      doi: cffText(pref.doi) || cffText(pref.url),
    },
  };
}

/* ------------------------------------------------------- GitHub REST API */

/** The parts of `GET /repos/{owner}/{repo}` the generator reads. */
export interface GithubRepo {
  name?: string;
  html_url?: string;
  default_branch?: string;
  owner?: { login?: string; type?: string };
}

/** The parts of `GET /repos/{owner}/{repo}/releases/latest` it reads. */
export interface GithubRelease {
  tag_name?: string;
  published_at?: string;
}

/** Values from the repository record alone, for a repository with no CITATION.cff. */
export function valuesFromRepo(
  repo: GithubRepo,
  release: GithubRelease | null
): Record<string, string> {
  const owner = repo.owner?.login ?? "";
  return {
    [F.authors]: repo.owner?.type === "Organization" ? `{${owner}}` : owner,
    [F.title]: repo.name ?? "",
    [F.version]: release?.tag_name ?? "",
    [F.date]: (release?.published_at ?? "").slice(0, 10),
    [F.publisher]: "GitHub",
    [F.doi]: "",
    [F.url]: repo.html_url ?? "",
  };
}

/* ------------------------------------------------------------- formatting */

export function githubIsReady(values: SpecialValues): boolean {
  return get(values, F.title) !== "";
}

interface Software {
  authors: PersonName[];
  title: string;
  version: string;
  date: ReturnType<typeof parsePartialDate>;
  publisher: string;
  /** The DOI as a link, or "". */
  doi: string;
  url: string;
}

function readSoftware(values: SpecialValues): Software {
  return {
    authors: parseSoftwareAuthors(get(values, F.authors)),
    title: get(values, F.title),
    version: get(values, F.version),
    date: parsePartialDate(get(values, F.date)),
    publisher: get(values, F.publisher),
    doi: doiUrl(get(values, F.doi)) ?? "",
    url: get(values, F.url),
  };
}

const RE_TAG_V = /^v(?=\d)/i;

/** "v1.2.0" (a release tag) → "1.2.0", so no style prints "Version v1.2.0". */
function versionNumber(version: string): string {
  return version.replace(RE_TAG_V, "");
}

function apaOutput(sw: Software, labels: GithubLabels): CitationOutput {
  const authors = apaAuthors(sw.authors);
  const version = sw.version ? ` (Version ${versionNumber(sw.version)})` : "";
  const link = sw.doi || sw.url;
  const source = [sw.publisher ? `${sw.publisher}.` : "", link]
    .filter(Boolean)
    .join(" ");
  const year = yearOf(sw.date);
  const reference: CitationParts = authors
    ? [
        plain(`${authors}${closer(authors)} (${year}). `),
        italic(sw.title),
        plain(`${version} [Computer software]. ${source}`),
      ]
    : [
        italic(sw.title),
        plain(`${version} [Computer software]. (${year}). ${source}`),
      ];
  const inText = (narrative: boolean): CitationParts => {
    if (sw.authors.length > 0) {
      return [plain(apaInText(sw.authors, year, narrative))];
    }
    return narrative
      ? [italic(sw.title), plain(` (${year})`)]
      : [plain("("), italic(sw.title), plain(`, ${year})`)];
  };
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: tidyParts(reference) },
      { label: labels.parenthetical, parts: inText(false) },
      { label: labels.narrative, parts: inText(true) },
    ],
    note: labels.apaNote,
  };
}

function mlaOutput(sw: Software, labels: GithubLabels): CitationOutput {
  const authors = mlaAuthors(sw.authors);
  const entry: CitationParts = [];
  if (authors) {
    entry.push(plain(`${authors}${closer(authors)} `));
  }
  entry.push(italic(sw.title), plain(closer(sw.title)));
  const version = sw.version ? `Version ${versionNumber(sw.version)}` : "";
  const date = sw.date ? mlaDate(sw.date) : "";
  if (sw.doi || sw.publisher !== "GitHub") {
    // Archived (e.g. Zenodo): the archive publishes it, and the DOI locates it.
    const facts = [version, sw.publisher, date, sw.doi || mlaUrl(sw.url)]
      .filter(Boolean)
      .join(", ");
    entry.push(plain(facts ? ` ${facts}.` : ""));
  } else {
    // On GitHub: the version and date, then GitHub as a second container.
    const facts = [version, date].filter(Boolean).join(", ");
    entry.push(plain(facts ? ` ${facts}.` : ""));
    entry.push(plain(" "), italic("GitHub"));
    entry.push(plain(sw.url ? `, ${mlaUrl(sw.url)}.` : "."));
  }
  const who = mlaInTextAuthors(sw.authors);
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(entry) },
      {
        label: labels.inText,
        parts: who
          ? [plain(`(${who})`)]
          : [plain("("), italic(sw.title), plain(")")],
      },
    ],
    note: labels.mlaNote,
  };
}

function chicagoOutput(sw: Software, labels: GithubLabels): CitationOutput {
  const year = sw.date ? String(sw.date.year) : "";
  const facts = [sw.publisher, year].filter(Boolean).join(", ");
  const link = sw.doi || sw.url;
  const version = versionNumber(sw.version);
  // A note leaves out a developer that is an organization (CMOS 18 §14.169, as the
  // CSL style implements it); the bibliography still lists the software under it.
  const groupOnly =
    sw.authors.length > 0 && sw.authors.every((name) => name.organization);
  const noteAuthors = groupOnly ? "" : chicagoNoteAuthors(sw.authors);
  const note: CitationParts = [
    plain(noteAuthors ? `${noteAuthors}, ` : ""),
    italic(sw.title),
    plain(
      `${version ? `, version ${version}` : ""}${facts ? ` (${facts})` : ""}${
        link ? `, ${link}` : ""
      }.`
    ),
  ];
  const bibAuthors = chicagoBibliographyAuthors(sw.authors);
  const bibliography: CitationParts = [
    plain(bibAuthors ? `${bibAuthors}${closer(bibAuthors)} ` : ""),
    italic(sw.title),
    plain(
      `${closer(sw.title)}${version ? ` Version ${version}.` : ""}${
        facts ? ` ${facts}.` : ""
      }${link ? ` ${link}.` : ""}`
    ),
  ];
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: tidyParts(note) },
      { label: labels.bibliography, parts: tidyParts(bibliography) },
    ],
    note: labels.chicagoNote,
  };
}

const BIB_OPTIONS: BibTextOptions = {
  protectCapitals: false,
  asciiSafe: false,
};

/** A DOI's bare form for BibTeX's `doi` field: "10.5281/zenodo.5171937". */
function bareDoi(link: string): string {
  return link.replace("https://doi.org/", "");
}

/** `@misc` for classic BibTeX and `@software` for biblatex. */
export function bibtexEntries(sw: Software): {
  misc: string;
  software: string;
} {
  const key = citationKey({
    author: sw.authors[0] ?? null,
    year: sw.date ? String(sw.date.year) : "",
    title: sw.title,
  });
  const author = joinNames(
    sw.authors.map((name) => bibtexName(name, BIB_OPTIONS))
  );
  // Double braces keep a software name's capitals exactly as written.
  const title = `{${bibText(sw.title, BIB_OPTIONS)}}`;
  const version = versionNumber(sw.version);
  const year = sw.date ? String(sw.date.year) : "";
  const doi = bareDoi(sw.doi);
  const misc = formatBibEntry("misc", key, [
    { name: "author", value: author },
    { name: "title", value: title },
    { name: "year", value: year },
    { name: "month", value: monthMacro(sw.date?.month), bare: true },
    { name: "howpublished", value: bibText(sw.publisher, BIB_OPTIONS) },
    {
      name: "note",
      value: version ? `Version ${bibText(version, BIB_OPTIONS)}` : "",
    },
    { name: "doi", value: doi },
    { name: "url", value: sw.url },
  ]);
  const isoDate = sw.date
    ? [
        String(sw.date.year),
        sw.date.month ? String(sw.date.month).padStart(2, "0") : "",
        sw.date.day ? String(sw.date.day).padStart(2, "0") : "",
      ]
        .filter(Boolean)
        .join("-")
    : "";
  const software = formatBibEntry("software", key, [
    { name: "author", value: author },
    { name: "title", value: title },
    { name: "version", value: bibText(version, BIB_OPTIONS) },
    { name: "date", value: isoDate },
    { name: "publisher", value: bibText(sw.publisher, BIB_OPTIONS) },
    { name: "doi", value: doi },
    { name: "url", value: sw.url },
  ]);
  return { misc, software };
}

export function formatGithub(
  values: SpecialValues,
  labels: GithubLabels
): CitationOutput[] {
  const sw = readSoftware(values);
  const bib = bibtexEntries(sw);
  return [
    apaOutput(sw, labels),
    mlaOutput(sw, labels),
    chicagoOutput(sw, labels),
    {
      key: "bibtex",
      label: labels.bibtex,
      entries: [
        { label: labels.bibtexMisc, parts: [plain(bib.misc)] },
        { label: labels.biblatexSoftware, parts: [plain(bib.software)] },
      ],
      note: labels.bibtexNote,
    },
  ];
}

/* --------------------------------------------- the preferred citation */

/** BibTeX `@article` (or `@misc`) for a CFF preferred citation. */
export function preferredBibtex(pref: PreferredCitation): string {
  const values = pref.values;
  const authors = parseSoftwareAuthors(values.authors);
  const key = citationKey({
    author: authors[0] ?? null,
    year: values.year,
    title: values.title,
  });
  const doiLink = doiUrl(values.doi);
  const type = pref.type === "article" ? "article" : "misc";
  return formatBibEntry(type, key, [
    {
      name: "author",
      value: joinNames(authors.map((name) => bibtexName(name, BIB_OPTIONS))),
    },
    { name: "title", value: `{${bibText(values.title, BIB_OPTIONS)}}` },
    {
      name: type === "article" ? "journal" : "howpublished",
      value: bibText(values.journal, BIB_OPTIONS),
    },
    { name: "volume", value: values.volume },
    { name: "number", value: values.issue },
    { name: "pages", value: values.pages.replace("–", "--") },
    { name: "year", value: values.year },
    { name: "doi", value: doiLink ? bareDoi(doiLink) : "" },
    { name: "url", value: doiLink ? "" : values.doi },
  ]);
}
